import React, { useState } from 'react';
import { Badge, ExpandableSection, SpaceBetween, StatusIndicator, StatusIndicatorProps } from '@amzn/awsui-components-core';
import { Avatar, AvatarSize } from '@amzn/stencil-react-components/avatar';
import { CheckStatus, CoverageCheckResult } from '../../hooks/useFolderChecks';
import { buildCoverageDisplayList } from '../../utils/readinessCheckUtils';
import { EmployeePopoverCard } from '../../../ea-huddle-v2/components/attendees/EmployeePopoverCard';
import { EmployeeListItem } from '../../../ea-huddle-v2/components/attendees/EmployeeListItem';
import { convertOrgChartEmployeeToEmployee } from '../../utils/employeeTypeAdapters';
import { OrgChartEmployee } from '../../types/orgChart.types';
import './CoverageRow.scss';

/** Maps an advisory CheckStatus to a Cloudscape StatusIndicator type. */
export const indicatorType = (status: CheckStatus): StatusIndicatorProps.Type =>
  status === 'success' ? 'success' : status === 'error' ? 'error' : status === 'warning' ? 'warning' : 'pending';

export interface CoverageRowProps {
  /** Label prefix for the summary line (e.g. the folder/category name). */
  title: string;
  /** The coverage result: covered/missing logins plus a rolled-up status. */
  result: CoverageCheckResult;
  /**
   * All org employees keyed by login, used to render each person with the same rich
   * {@link EmployeeListItem} treatment the Step 2 org chart uses (avatar + name + `[alias]` + Slack
   * + details line). A login absent here falls back to a bare avatar so the row never breaks.
   */
  employees: Map<string, OrgChartEmployee>;
}

/**
 * A single coverage check row for an OPTIONAL source folder: an info StatusIndicator summarizing how
 * many selected people have material added, with an ExpandableSection listing the not-yet-added
 * people as avatars (collapsed by default) when any are missing. Each avatar shows the person's
 * detail on hover via EmployeePopoverCard.
 *
 * The severity is always `info` (never `warning`) — supplemental material is optional, so partial
 * coverage is informational, not a problem to flag. Recovered from the former ReadinessCheckList;
 * now reused inline in Step 4 next to the matching card-category checkbox.
 */
export const CoverageRow: React.FC<CoverageRowProps> = ({ title, result, employees }) => {
  const [expanded, setExpanded] = useState(false);
  const total = result.covered.length + result.missing.length;
  // Flatten to a single included-first list so covered people appear at the top; each entry
  // carries an `included` flag that drives its green/red badge.
  const entries = buildCoverageDisplayList(result);

  return (
    // Class-bearing wrapper so the scoped SCSS can shrink the "Included & Missing (N)"
    // ExpandableSection header text (Cloudscape's footer-variant header inherits font-size).
    <div className="coverage-row">
      <SpaceBetween size="xxs">
        <StatusIndicator type="info">{`Added for ${result.covered.length} of ${total} people · optional`}</StatusIndicator>

        {total > 0 && (
          <ExpandableSection
            variant="footer"
            headerText={`Included & Missing (${total})`}
            expanded={expanded}
            headingTagOverride="h3"
            onChange={() => setExpanded((prev) => !prev)}
          >
            {/* One employee per line (vertical stack) rather than a wrapping horizontal row. */}
            <SpaceBetween size="xs">
              {entries.map(({ login, included }) => {
                const badge = <Badge color={included ? 'green' : 'red'}>{included ? 'Included' : 'Missing'}</Badge>;
                const employee = employees.get(login);
                // Render each person with the SAME rich treatment as the Step 2 org chart
                // (EmployeeCardNode): the shared EmployeeListItem, with the Included/Missing badge in
                // its right-aligned `renderActions` slot. Match Step 2's props exactly
                // (showLevel={false} levelInlineBeforeCity disableHover). If the login isn't in the
                // employees map (edge case), fall back to a bare avatar + badge so the row survives.
                return employee ? (
                  <EmployeeListItem
                    key={login}
                    employee={convertOrgChartEmployeeToEmployee(employee)}
                    showLevel={false}
                    levelInlineBeforeCity
                    disableHover
                    renderActions={() => badge}
                  />
                ) : (
                  <div key={login} className="coverage-row__person">
                    <EmployeePopoverCard loginName={login} renderWithPortal>
                      <Avatar username={login} size={AvatarSize.ExtraSmall} imageAltText={`Profile image of ${login}`} />
                    </EmployeePopoverCard>
                    {badge}
                  </div>
                );
              })}
            </SpaceBetween>
          </ExpandableSection>
        )}
      </SpaceBetween>
    </div>
  );
};
